import {ActivatedRoute, Router} from '@angular/router';
import {Component, inject, OnDestroy, OnInit, signal, ViewEncapsulation} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {AuthenticationService} from '../authentication.service';
import {DataEntity} from 'octopus-connect';
import {FuseConfirmDialogComponent} from 'fuse-core/components/confirm-dialog/confirm-dialog.component';
import {Subject} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {currentTimestamp} from 'shared/utils/datetime';
import {fuseAnimations} from 'fuse-core/animations';
import {takeUntil} from 'rxjs/operators';
import {LocalCacheService} from '@modules/authentication/core/services/local-cache.service';
import {CachedUser} from '@modules/authentication/core/models/cached-user.type';
import {
    AuthenticationConfigurationService
} from '@modules/authentication/core/config/authentication-configuration.service';
import {CommunicationCenterService} from '@modules/communication-center';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    standalone: false
})
export class FuseLoginComponent implements OnInit, OnDestroy {

    private router = inject(Router);
    private dialog = inject(MatDialog);
    private route = inject(ActivatedRoute);
    private translate = inject(TranslateService);
    private localCache = inject(LocalCacheService);
    private authenticationService = inject(AuthenticationService);
    private communicationCenter = inject(CommunicationCenterService);
    private configuration = inject(AuthenticationConfigurationService);

    public renewPassword = false;
    public modeSSO = false;
    public isLoading = false;
    public entButtonAllowed = false; // only allowed for fr language
    public settings = this.configuration.settings
    public forceLoginValue = signal<string>(null);

    private hideSessions = false;
    private unsubscribeInTakeUntil = new Subject<void>();

    /***** GETTERS *****/

    public get displayGARHelper(): boolean {
        return this.configuration.displayGARHelper;
    }

    public get displaySessions(): boolean {
        return this.localCache.hasCachedUsers && !this.hideSessions && !this.renewPassword;
    }

    private get entButtonAllowedWithoutRules(): boolean {
        return this.settings?.entButtonAllowedWithoutRules;
    }

    /***** LIFECYCLE HOOKS *****/

    ngOnInit(): void {
        //ctz-setting
        if (this.entButtonAllowedWithoutRules) {
            this.entButtonAllowed = true;
        } else {
            //citizen-code case
            this.entButtonAllowed = localStorage.getItem('lang') === 'fr' || localStorage.getItem('lang') === null;
            this.communicationCenter.getRoom('current-language').getSubject('id').subscribe(lng => {
                if (lng === 'fr') {
                    this.entButtonAllowed = true;
                } else {
                    this.entButtonAllowed = false;
                }
            });
        }
        if (this.route.snapshot.queryParams.code) {
            this.modeSSO = true;
            this.authenticationService.loginSSO(this.route.snapshot.queryParams.code);
        }
    }

    ngOnDestroy(): void {
        this.isLoading = false;
        this.unsubscribeInTakeUntil.next();
        this.unsubscribeInTakeUntil.complete();
    }

    /***** LOGIC *****/

    /**
     * login user : with optionnal change password possibility force by rules
     */
    private doLogin(label: string, pass: string): void {
        this.isLoading = true;

        if (label && pass) {
            this.authenticationService
                .authenticateIn(
                    'http',
                    label,
                    pass
                )
                .pipe(takeUntil(this.unsubscribeInTakeUntil))
                .subscribe((user) => {
                    if (
                        this.settings.validateEmailStrategyActivated &&
                        !user.get('email_status')
                    ) {
                        this.authenticationService.logoutFrom('http');
                        this.isLoading = false;
                        return;
                    }

                    if (this.isExpirePassword(user)) {
                        this.renewPassword = true;
                        this.authenticationService.logoutFrom('http');
                        this.isLoading = false;
                        return;
                    }

                    const isTrainer = !!(user.get('role').findIndex((role) => role === 5) >= 0); // tester autrement qu'avec un numero (voir authService)
                    // it's fisrts user trainer connexion and help is active
                    if (
                        user.get('access') &&
                        user.get('first_access') === true &&
                        this.settings.askForHelp &&
                        isTrainer
                    ) {
                        this.help();
                    } else {
                        this.authenticationService.navigateAfterLogin();
                    }
                }, error => {
                    console.error(error);
                    this.isLoading = false;
                });
        }
    }

    /**
     * open modal asking if user need help at first connexion
     */
    private help(): void {
        const data = {
            titleDialog: 'generic.ask.help',
            bodyDialog: 'generic.ask.help.content',
            labelTrueDialog: 'generic.yes',
            labelFalseDialog: 'generic.no',
        };

        this.translate
            .get(data.titleDialog)
            .subscribe((translation: string) => (data.titleDialog = translation));
        this.translate
            .get(data.bodyDialog)
            .subscribe((translation: string) => (data.bodyDialog = translation));
        this.translate
            .get(data.labelTrueDialog)
            .subscribe((translation: string) => (data.labelTrueDialog = translation));
        this.translate
            .get(data.labelFalseDialog)
            .subscribe(
                (translation: string) => (data.labelFalseDialog = translation)
            );

        const confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, {
            data,
        });

        confirmDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                // help page
                this.router.navigate(['home']);
            } else {
                // normal navigate
                this.authenticationService.navigateAfterLogin();
            }
        });
    }

    /**
     * is user expiration password date is passed
     */
    private isExpirePassword(user: DataEntity): boolean {
        return (
            this.settings.activeChangePasswordStrategy &&
            user.get('expirePassword') &&
            currentTimestamp() >= user.get('expirePassword') &&
            user.get('expirePassword') !== null &&
            user.get('expirePassword') !== undefined
        );
    }


    /***** EVENTS *****/

    public onLoginSession(user: CachedUser): void {
        if (user?.codeid) {
            this.doLogin(`tralacodeid${user.label}`, user.codeid);
        } else {
            if (user) {
                this.forceLoginValue.set(user.label);
            }
            this.hideSessions = true;
        }
    }


}